import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { faqItems } from '@/mocks/faq';

export const PREVIEW_IDS = ['free', 'performance', 'strategy', 'balance'];

export default function FaqPreview() {
  const items = faqItems.filter((item) => PREVIEW_IDS.includes(item.id));
  const [openId, setOpenId] = useState<string>(items[0]?.id ?? '');

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? '' : id));

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-start">
          <Reveal>
            <SectionHeading
              align="left"
              eyebrow="Before you ask"
              title="The questions every gold trader brings up"
              subtitle="Straight answers about cost, risk and setup — the same ones our support chat gets every day."
            />
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-accent-400 hover:bg-accent-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 transition-colors whitespace-nowrap"
            >
              Read all questions
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </Reveal>

          <Reveal delay={120}>
            <div className="flex flex-col gap-3">
              {items.map((item) => {
                const open = openId === item.id;
                const buttonId = `home-${item.id}-button`;
                const panelId = `home-${item.id}-panel`;
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg border transition-colors duration-200 ${
                      open ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <h3>
                      <button
                        type="button"
                        id={buttonId}
                        onClick={() => toggle(item.id)}
                        aria-expanded={open}
                        aria-controls={panelId}
                        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-100 rounded-lg"
                      >
                        <span className="font-heading text-sm md:text-base font-semibold text-foreground-950">
                          {item.question}
                        </span>
                        <span
                          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                            open ? 'bg-accent-500 text-primary-950 rotate-180' : 'bg-background-200 text-foreground-700'
                          }`}
                        >
                          <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
                        </span>
                      </button>
                    </h3>
                    {/* Auto-height reveal via grid rows — matches the FAQ page so longer
                        answers can never be clipped. */}
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className={`grid transition-all duration-300 ease-out ${
                        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-5 text-xs md:text-sm leading-relaxed text-foreground-600">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}